import type { AnyAction, ThunkAction } from '@reduxjs/toolkit'

import {
  EndpointArgs,
  InferMutationDefinition,
  MutationDefinition,
  UseMutationResult
} from '../../../store/serverApi/types'

import {
  MutationActionCreatorResult,
  UseMutation,
  UseMutationStateOptions
} from '../../../types/rtkInternals'

import { useDispatch } from 'react-redux'
import { FeedbotDispatch } from '../../../store/types'

import { RootState } from '../../../reducers'

type ConstructorParameters<Endpoint extends MutationDefinition> = {
  endpoint: MutationDefinition
  argsTransformer?: (state: RootState, newParams: any) => EndpointArgs<Endpoint>
  skipCriteria?: (endpointArgs: EndpointArgs<Endpoint>) => boolean
  fixedCacheKeyGetter?: (endpointArgs: EndpointArgs<Endpoint>) => string
}

type Types<Endpoint extends MutationDefinition> = {
  endpoint: Endpoint
  endpointDefinition: InferMutationDefinition<Endpoint>
  nativeArgs: EndpointArgs<Endpoint>
  outerArgs: any
}

type GetTypesFromConstructorParams<CP extends ConstructorParameters<MutationDefinition>> = {
  endpoint: CP['endpoint']
  endpointDefinition: InferMutationDefinition<CP['endpoint']>
  nativeArgs: EndpointArgs<CP['endpoint']>
  outerArgs: CP['argsTransformer'] extends Function
    ? Parameters<CP['argsTransformer']>[1]
    : EndpointArgs<CP['endpoint']>
}

class MutationEndpointHelper<
  T extends Types<any>,
  ConstructorParams extends ConstructorParameters<T['endpoint']>
> {
  private endpoint: T['endpoint']
  private readonly params: ConstructorParams
  private readonly transformArgs: (state: RootState, newParams: T['outerArgs']) => T['nativeArgs']
  private readonly shouldSkip: (endpointArgs: T['nativeArgs'], state: RootState) => boolean
  public readonly getFixedCacheKey?: (endpointArgs: T['nativeArgs']) => string

  constructor(params: ConstructorParams) {
    this.params = params
    this.endpoint = params.endpoint
    this.transformArgs = params.argsTransformer ?? ((state, params) => params)
    this.shouldSkip = params?.skipCriteria ?? (() => false)
    this.getFixedCacheKey = params?.fixedCacheKeyGetter
  }

  public static new = <CP extends ConstructorParameters<any>>(params: CP) => {
    // This is a TypeScript workaround - class generic types cannot be inferred from constructor parameter.
    // Using this method instead of `new QueryEndpointHelper()` makes it possible to infer.
    return new MutationEndpointHelper<GetTypesFromConstructorParams<CP>, CP>(params)
  }

  // --- Class management methods ---

  public addArgsTransformer = <OuterParams>(
    argsTransformer: (state: RootState, params: OuterParams) => T['nativeArgs']
  ) => {
    return MutationEndpointHelper.new({ ...this.params, argsTransformer })
  }

  public addSkipCondition = (skipCriteria: typeof this.shouldSkip) => {
    return MutationEndpointHelper.new({ ...this.params, skipCriteria })
  }

  public addFixedCacheKeyGetter = (fixedCacheKeyGetter: typeof this.getFixedCacheKey) => {
    return MutationEndpointHelper.new({ ...this.params, fixedCacheKeyGetter })
  }

  // --- Api control methods ---

  public initiate = (
    callSiteArgs: T['outerArgs']
  ): ThunkAction<MutationActionCreatorResult<T['endpointDefinition']>, any, void, AnyAction> => {
    // CAREFUL: DO NOT USE ASYNC FUNCTION HERE! Async function returns its own promise.
    // We need to return the native rtkq promise to have access to the `.unwrap()` method
    // @ts-ignore
    return (dispatch: FeedbotDispatch, getState: () => RootState) => {
      const args = this.transformArgs(getState(), callSiteArgs)
      if (this.shouldSkip(args, getState())) {
        return
      }

      if (this.getFixedCacheKey) {
        const fixedCacheKey = this.getEndpointHelperFixedCacheKey(args)
        return dispatch(this.endpoint.initiate(args, { fixedCacheKey }))
      }

      return dispatch(this.endpoint.initiate(args))
    }
  }

  // With this method, fixedCacheKey needs to be provided manually as useMutation({ fixedCacheKey })
  public useMutation = (options?: UseMutationStateOptions<T['endpointDefinition'], any>) => {
    const dispatch = useDispatch()
    const useMutation = this.endpoint.useMutation as UseMutation<T['endpointDefinition']>
    const [nativeTrigger, nativeResult] = useMutation(options)

    return [
      (param: T['outerArgs']) => {
        // @ts-ignore
        const getState: () => RootState = dispatch((dispatch, getState) => getState)
        const args = this.transformArgs(getState(), param)

        if (this.shouldSkip(args, getState())) {
          return
        }

        return nativeTrigger(args as any)
      },
      nativeResult as UseMutationResult<T['endpoint']>
    ] as const
  }

  public fixedKeySelector = (callSiteArgs: T['outerArgs']) => {
    if (!this.getFixedCacheKey) {
      throw new Error(
        'fixedKeySelector can only be used with endpoints that have a getFixedCacheKey mapper'
      )
    }

    return (state: RootState) => {
      const args = this.transformArgs(state, callSiteArgs)
      const fixedCacheKey = this.getEndpointHelperFixedCacheKey(args)
      // @ts-ignore
      return this.endpoint.select({ fixedCacheKey })(state as any)
    }
  }

  private getEndpointHelperFixedCacheKey(endpointArgs: T['nativeArgs']): string {
    return this.endpoint.name + ':' + this.getFixedCacheKey?.(endpointArgs)
  }
}

export function createMutationHelper<Endpoint extends MutationDefinition>(endpoint: Endpoint) {
  return MutationEndpointHelper.new({ endpoint })
}
